
import React, { useState, useEffect } from "react"
import { Modal, Image, Table, Tabs } from "antd"
import { X, Briefcase, Layers, User, FileText, DollarSign, BarChart3, Download, ExternalLink } from "lucide-react"
import moment from "moment"
import { AiFillFilePdf } from "react-icons/ai"
import { FaFileWord } from "react-icons/fa"
import PropTypes from "prop-types"
import { getAllowancesByEmployeeId } from "../../../service/allowanceService"
import { getDeductionsByEmployeeId } from "../../../service/deductionService"

const { TabPane } = Tabs

const EmployeeViewModal = ({
  isVisible,
  onClose,
  formData,
  uploadedPic,
  resignData,
  allowanceData,
  budgetaryAllowance,
  totalBasic,
  shiftHistory,
  documents,
  education_and_training,
    departments,
  designations,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState("")
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [activeTab, setActiveTab] = useState("1")
  const [localAllowanceData, setLocalAllowanceData] = useState(allowanceData || [])
  const [localDeductionData, setLocalDeductionData] = useState([])
  const employeeId = formData?.employeeId

  const handlePreview3 = (file) => {
    setPreviewFile(file)
    setPreviewVisible(true)
  }

  useEffect(() => {
    setLocalAllowanceData(allowanceData || [])
  }, [allowanceData])

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    if (!employeeId) {
      setLocalAllowanceData(allowanceData || [])
      return undefined
    }

    let isActive = true

    const fetchData = async () => {
      try {
        const allowancesPromise = getAllowancesByEmployeeId(employeeId)
          .catch((err) => {
            console.error("Error fetching allowances:", err);
            return [];
          });
        const deductionsPromise = getDeductionsByEmployeeId(employeeId)
          .catch((err) => {
            console.error("Error fetching deductions:", err);
            return [];
          });

        const [allowances, deductions] = await Promise.all([
          allowancesPromise,
          deductionsPromise,
        ]);

        if (isActive) {
          setLocalAllowanceData(allowances || []);
          setLocalDeductionData(deductions || []);
        }
      } catch (error) {
        console.error("General error in fetchData:", error);
      }
    }

    fetchData()

    return () => {
      isActive = false
    }
  }, [isVisible, employeeId, allowanceData])

  const renderFileIconForView = (file) => {
    const fileType = file.split(".").pop().toLowerCase()
    if (fileType === "pdf") {
      return (
        <AiFillFilePdf style={{ fontSize: 24, color: "red", cursor: "pointer" }} onClick={() => handlePreview3(file)} />
      )
    } else if (fileType === "doc" || fileType === "docx") {
      return <FaFileWord style={{ fontSize: 24, color: "blue" }} />
    } else {
      return <p>{file.name}</p>
    }
  }

  const shiftHistoryColumns = [
    {
      title: "Shift",
      dataIndex: ["newShiftId", "shiftName"],
      key: "newShift",
      render: (text) => text || "N/A",
    },
    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
      render: (text) => (text ? moment(text).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (text) => text || "N/A",
    },
  ]

  const historyColumn = [
    {
      title: "Date",
      dataIndex: "resignDate",
      key: "resignDate",
      render: (text) => {
        const formattedDate = text ? new Date(text).toISOString().split("T")[0] : "N/A"
        return formattedDate
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        return text && text.trim() !== "" ? text : "-"
      },
    },
  ]

  const AllowanceColumn = [
    {
      title: "Name",
      dataIndex: "allowanceType",
      key: "allowanceName",
      render: (allowanceType) => allowanceType?.name || "—",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (val) => val?.toLocaleString() || "0",
    },
    {
      title: "Allowance Type",
      dataIndex: "allowanceType",
      key: "type",
      render: (allowanceType) => allowanceType?.allowanceType || "—",
    },
    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
      render: (val) => val ? moment(val).format("YYYY-MM-DD") : "—",
    },
  ]

  const columns2 = [
    {
      title: "Type of Qualification",
      dataIndex: "label",
      key: "label",
      render: (text) => (
        <>
          {text} <p style={{ display: "inline", color: "#C0C0C0" }}>{`(Optional)`}</p>
        </>
      ),
    },
    {
      title: "Uploaded Docs/Pdfs",
      key: "upload",
      render: (_, record) => (
        <>
          {documents
            .filter((doc) => doc.type === record.value)
            .map((file, index) => (
              <div
                key={index}
                style={{
                  marginTop: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    marginTop: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {renderFileIconForView(file.url || file.name)}
                  <p style={{ marginLeft: 8, marginTop: "0px" }}>{file.name}</p>
                </div>
              </div>
            ))}
        </>
      ),
    },
  ]

  const PersonalInfoContent = () => (
    <div style={{ padding: "20px", backgroundColor: "#fff" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          marginBottom: "30px",
          gap: "20px",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <Image
            src={uploadedPic || "/placeholder.svg?height=120&width=120"}
            alt="Profile Picture"
            width={120}
            height={120}
            style={{
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
            }}
            preview={{
              zIndex: 10000,
              visible: previewOpen,
              src: previewImage,
              onVisibleChange: (visible) => setPreviewOpen(visible),
            }}
            onClick={() => {
              setPreviewImage(uploadedPic)
              setPreviewOpen(true)
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Employee Name</span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#ff6b35" }}>{formData.full_name}</span>
          </div>
            <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Employee EPF</span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#ff6b35" }}>{formData.epfNumber}</span>
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>NIC</span>
            <span style={{ fontSize: "14px", color: "#333" }}>{formData.nic}</span>
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Email</span>
            <span style={{ fontSize: "14px", color: "#333" }}>{formData.email}</span>
          </div>
          <div>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Contact</span>
            <span style={{ fontSize: "14px", color: "#333" }}>{formData.contactNumber}</span>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#333",
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: "10px",
          }}
        >
           Employee Details
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Date of birth</div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {formData.dateOfBirth ? moment(formData.dateOfBirth, "YYYY-MM-DD").format("YYYY MM DD") : "N/A"}
            </div>
          </div>
               <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Marital Status</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.marital_status}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Nationality</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.nationality}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Gender</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.gender}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Branch</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.branch}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Employee Category</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.employeeCategory}</div>
          </div>
          <div style={{display:"none"}}>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Date of Termination</div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {formData.dateOfTermination
                ? moment(formData.dateOfTermination, "YYYY-MM-DD").format("YYYY MM DD")
                : "N/A"}
            </div>
          </div>
                <div >
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Joined Date</div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {formData.joiningDate
                ? moment(formData.joiningDate, "YYYY-MM-DD").format("YYYY MM DD")
                : "N/A"}
            </div>
          </div>
               <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Resign Date</div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {formData.resign_date
                ? moment(formData.resign_date, "YYYY-MM-DD").format("YYYY MM DD")
                : "N/A"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Employee Type</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.employeeType}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Center</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.center}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Department</div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {formData.department?.departmentName || 
               departments.find(dep => dep._id === formData.departmentId)?.departmentName || 
               "N/A"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Designation</div>
            <div style={{ fontSize: "14px", color: "#333" }}>
              {formData.designation?.designationName || 
               designations.find(des => des._id === formData.designationId)?.designationName || 
               "N/A"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Pay Frequency</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.payFrequency || "N/A"}</div>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#333",
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: "10px",
          }}
        >
          Bank Information
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Holder Name</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.accountHolderName}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Account Number</div>
            <div style={{ fontSize: "14px", color: "#ff6b35", fontWeight: "500" }}>{formData.accountNumber}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Bank</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.bankName}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Branch name</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.branchLocation}</div>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#333",
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: "10px",
          }}
        >
          Other Information
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>

             <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Emergency Contact Person</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.emergency_name} | {formData.emergency_relationship}</div>
          </div>
             <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Emergency No 1</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.emergencyNo1}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Emergency No 2</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.emergencyNo2 || "N/A"}</div>
          </div>

       
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Emergency No 2</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.emergencyNo2 || "N/A"}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Permanent Address</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.address}</div>
          </div>
           <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Residential Address</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.residenceAddress}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>City</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.city}</div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px" }}>Province</div>
            <div style={{ fontSize: "14px", color: "#333" }}>{formData.province}</div>
          </div>
        </div>
      </div>
    </div>
  )

  const DocumentsContent = () => (
    <div style={{ padding: "20px", backgroundColor: "#fff" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          marginBottom: "30px",
          gap: "20px",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <Image
            src={uploadedPic || "/placeholder.svg?height=120&width=120"}
            alt="Profile Picture"
            width={120}
            height={120}
            style={{
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Employee Name</span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#ff6b35" }}>{formData.full_name}</span>
          </div>
           <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Employee EPF</span>
            <span style={{ fontSize: "16px", fontWeight: "600", color: "#ff6b35" }}>{formData.epfNumber}</span>
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>NIC</span>
            <span style={{ fontSize: "14px", color: "#333" }}>{formData.nic}</span>
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Email</span>
            <span style={{ fontSize: "14px", color: "#333" }}>{formData.email}</span>
          </div>
          <div>
            <span style={{ fontSize: "12px", color: "#999", display: "block" }}>Contact</span>
            <span style={{ fontSize: "14px", color: "#333" }}>{formData.contactNumber}</span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "30px" }}>
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#333",
          }}
        >
          Professional Documents
        </h3>

        <div style={{ display: "none", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "20px" }}>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px" }}>
              <Download size={16} style={{ color: "#666", cursor: "pointer" }} />
              <ExternalLink size={16} style={{ color: "#666", cursor: "pointer" }} />
            </div>
            <div style={{ fontSize: "48px", color: "#dc3545", marginBottom: "10px" }}>PDF</div>
            <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "5px" }}>Document name</div>
            <div style={{ fontSize: "12px", color: "#666" }}>Description about document</div>
          </div>

          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px" }}>
              <Download size={16} style={{ color: "#666", cursor: "pointer" }} />
              <ExternalLink size={16} style={{ color: "#666", cursor: "pointer" }} />
            </div>
            <div style={{ fontSize: "48px", color: "#dc3545", marginBottom: "10px" }}>PDF</div>
            <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "5px" }}>Document name</div>
            <div style={{ fontSize: "12px", color: "#666" }}>Description about document</div>
          </div>
        </div>
      </div>

      <div>
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#333",
          }}
        >
          Competencies Documents
        </h3>

        <div style={{ display: "none", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "20px" }}>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px" }}>
              <Download size={16} style={{ color: "#666", cursor: "pointer" }} />
              <ExternalLink size={16} style={{ color: "#666", cursor: "pointer" }} />
            </div>
            <div style={{ fontSize: "48px", color: "#dc3545", marginBottom: "10px" }}>PDF</div>
            <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "5px" }}>Document name</div>
            <div style={{ fontSize: "12px", color: "#666" }}>Description about document</div>
          </div>

          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px" }}>
              <Download size={16} style={{ color: "#666", cursor: "pointer" }} />
              <ExternalLink size={16} style={{ color: "#666", cursor: "pointer" }} />
            </div>
            <div style={{ fontSize: "48px", color: "#dc3545", marginBottom: "10px" }}>PDF</div>
            <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "5px" }}>Document name</div>
            <div style={{ fontSize: "12px", color: "#666" }}>Description about document</div>
          </div>

          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px" }}>
              <Download size={16} style={{ color: "#666", cursor: "pointer" }} />
              <ExternalLink size={16} style={{ color: "#666", cursor: "pointer" }} />
            </div>
            <div style={{ fontSize: "48px", color: "#dc3545", marginBottom: "10px" }}>PDF</div>
            <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "5px" }}>Document name</div>
            <div style={{ fontSize: "12px", color: "#666" }}>Description about document</div>
          </div>

          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px" }}>
              <Download size={16} style={{ color: "#666", cursor: "pointer" }} />
              <ExternalLink size={16} style={{ color: "#666", cursor: "pointer" }} />
            </div>
            <div style={{ fontSize: "48px", color: "#dc3545", marginBottom: "10px" }}>PDF</div>
            <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "5px" }}>Document name</div>
            <div style={{ fontSize: "12px", color: "#666" }}>Description about document</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "30px" }}>
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "20px",
            color: "#333",
          }}
        >
          Education and Training
        </h3>
        <Table columns={columns2} dataSource={education_and_training} rowKey="value" pagination={false} />
      </div>
    </div>
  )

  const SalarySlipsContent = () => (
    <div>
      <div className="row" style={{ marginTop: "10px" }}>
        <div className="card-title-head">
          <h6>
            <span>
              <Briefcase className="feather-dollar-sign" />
            </span>
            Compensation and Benefits
          </h6>
        </div>

        {/* Full Time: show EPF, Tax, OT fields */}
        {formData.employeeCategory === "Full Time" && (
          <>
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">EPF Number</label>
                <input disabled={true} type="text" className="form-control" value={formData.epfNumber || "N/A"} />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">Basic Salary</label>
                <input disabled={true} type="text" className="form-control" value={formData.basicSalary} />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">Budgetary Allowance</label>
                <input
                  disabled={true}
                  type="text"
                  className="form-control"
                  value={budgetaryAllowance?.totalAllowance || 0}
                />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">Total Basic</label>
                <input disabled={true} type="text" className="form-control" value={totalBasic || 0} />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">Payee Tax</label>
                <input
                  disabled={true}
                  type="text"
                  className="form-control"
                  value={formData.payeeTaxApplicable ? "Applicable" : "Not Applicable"}
                />
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="mb-3">
                <label className="form-label">Overtime Eligibility</label>
                <input
                  disabled={true}
                  type="text"
                  className="form-control"
                  value={formData.overtimeEligibility ? "Eligible" : "Not Eligible"}
                />
              </div>
            </div>
          </>
        )}

        {/* Allowances table */}
        <h6 style={{ marginTop: "10px" }}>Allowances</h6>
        <Table columns={AllowanceColumn} dataSource={localAllowanceData || []} rowKey="_id" pagination={false} />

        {/* Deductions table */}
        <h6 style={{ marginTop: "20px" }}>Deductions</h6>
        <Table
          columns={[
            {
              title: "Deduction Type",
              key: "type",
              render: (_, record) => record.parentId?.deducitionType || "—",
            },
            {
              title: "Payment Type",
              dataIndex: "paymentType",
              key: "paymentType",
              render: (val) => val || "—",
            },
            {
              title: "Amount (LKR)",
              dataIndex: "amount",
              key: "amount",
              render: (val) => val?.toLocaleString() || "0",
            },
            {
              title: "Effective Date",
              dataIndex: "deduction_date",
              key: "deduction_date",
              render: (val) => val || "—",
            },
          ]}
          dataSource={localDeductionData || []}
          rowKey="_id"
          pagination={false}
          locale={{ emptyText: "No deductions found" }}
        />
      </div>

      {shiftHistory && shiftHistory.length > 0 && (
        <div className="row" style={{ marginTop: "10px" }}>
          <div className="card-title-head">
            <h6>Shift History</h6>
          </div>
          <Table
            columns={shiftHistoryColumns}
            dataSource={shiftHistory}
            rowKey={(record, index) => index}
            pagination={false}
          />
        </div>
      )}
    </div>
  )

  const PerformanceContent = () => (
    <div>
      {resignData && resignData.length > 0 && (
        <div className="row" style={{ marginTop: "10px" }}>
          <div className="card-title-head">
            <h6>
              <span>
                <Layers className="feather-layers" />
              </span>
              Employee History
            </h6>
          </div>
          <Table columns={historyColumn} dataSource={resignData} rowKey="value" pagination={false} />
        </div>
      )}
    </div>
  )

  return (
    <>
      <Modal
        zIndex={1200}
        open={isVisible}
        onCancel={onClose}
        width="75vw"
        footer={null}
        maskClosable={false}
        closable={false}
        styles={{
          body: { padding: 0 },
        }}
      >
        <div style={{ backgroundColor: "#fff" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 20px 0 20px",
              // borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#333" }}>View Employee</h2>
            </div>
            <X
              style={{
                fontSize: "20px",
                color: "#ff6b35",
                cursor: "pointer",
              }}
              onClick={onClose}
            />
          </div>

          <div style={{ padding: "0 20px" ,marginTop:"10px"}}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              style={{ marginTop: "0" }}
              tabBarStyle={{
                borderBottom: "0px solid #f0f0f0",
                marginBottom: 0,
              }}
            >
              <TabPane
                tab={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: activeTab === "1" ? "#ff6b35" : "#666",
                      fontWeight: activeTab === "1" ? "500" : "400",
                    }}
                  >
                    <User size={16} />
                    Employee Info
                  </span>
                }
                key="1"
              >
                <PersonalInfoContent />
              </TabPane>

              <TabPane
                tab={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: activeTab === "2" ? "#ff6b35" : "#666",
                      fontWeight: activeTab === "2" ? "500" : "400",
                    }}
                  >
                    <FileText size={16} />
                    Documents
                  </span>
                }
                key="2"
              >
                <DocumentsContent />
              </TabPane>

              <TabPane
                tab={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: activeTab === "3" ? "#ff6b35" : "#666",
                      fontWeight: activeTab === "3" ? "500" : "400",
                    }}
                  >
                    <DollarSign size={16} />
                    Compensation
                  </span>
                }
                key="3"
              >
                <SalarySlipsContent />
              </TabPane>

              <TabPane
                tab={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: activeTab === "4" ? "#ff6b35" : "#666",
                      fontWeight: activeTab === "4" ? "500" : "400",
                    }}
                  >
                    <BarChart3 size={16} />
                    Performance
                  </span>
                }
                key="4"
              >
                <PerformanceContent />
              </TabPane>
            </Tabs>
          </div>
        </div>
      </Modal>

      <Modal
        open={previewVisible}
        title="Document Preview"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20 }}
      >
        {previewFile && (
          <iframe src={previewFile} style={{ width: "100%", height: "600px" }} title="Document Preview" />
        )}
      </Modal>
    </>
  )
}

EmployeeViewModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    full_name: PropTypes.string,
        epfNumber: PropTypes.string,

    nic: PropTypes.string,
    email: PropTypes.string,
    dateOfBirth: PropTypes.string,
    gender: PropTypes.string,
    nationality: PropTypes.string,
    contactNumber: PropTypes.string,
    branch: PropTypes.string,
    joiningDate: PropTypes.string,
        marital_status: PropTypes.string,

    dateOfTermination: PropTypes.string,
        resign_date: PropTypes.Date,
    employeeCategory: PropTypes.string,
    employeeType: PropTypes.string,
    accountHolderName: PropTypes.string,
    center: PropTypes.string,    accountNumber: PropTypes.string,
    bankName: PropTypes.string,
    branchLocation: PropTypes.string,
    emergencyNo1: PropTypes.string,
    emergencyNo2: PropTypes.string,
    emergency_relationship:PropTypes.string,
    emergency_name:PropTypes.string,
    address: PropTypes.string,
    residenceAddress:PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    basicSalary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        department: PropTypes.shape({
      _id: PropTypes.string,
      departmentName: PropTypes.string,
    }),
    designation: PropTypes.shape({
      _id: PropTypes.string,
      designationName: PropTypes.string,
    }),
    departmentId: PropTypes.string,
    designationId: PropTypes.string,
    payFrequency: PropTypes.string,
        employeeId: PropTypes.string,
    payeeTaxApplicable: PropTypes.bool,
    overtimeEligibility: PropTypes.bool,
  }).isRequired,
  uploadedPic: PropTypes.string,
  resignData: PropTypes.arrayOf(
    PropTypes.shape({
      resignDate: PropTypes.string,
      status: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  allowanceData: PropTypes.arrayOf(
    PropTypes.shape({
      allowanceType: PropTypes.shape({
        name: PropTypes.string,
        allowanceType: PropTypes.string,
      }),
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  budgetaryAllowance: PropTypes.shape({
    totalAllowance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  totalBasic: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shiftHistory: PropTypes.arrayOf(
    PropTypes.shape({
      newShiftId: PropTypes.shape({
        shiftName: PropTypes.string,
      }),
      effectiveDate: PropTypes.string,
      reason: PropTypes.string,
    }),
  ),
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      url: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  education_and_training: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
    departments: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      departmentName: PropTypes.string.isRequired,
    }),
  ),
  designations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      designationName: PropTypes.string.isRequired,
    }),
  ),
}

EmployeeViewModal.defaultProps = {
  uploadedPic: "",
  resignData: [],
  allowanceData: [],
  budgetaryAllowance: { totalAllowance: 0 },
  totalBasic: 0,
  shiftHistory: [],
  documents: [],
  education_and_training: [],
    departments: [],
  designations: [],
}

export default EmployeeViewModal
